/**
 * Global application context and state management
 */
import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AppContext = createContext();

// Initial state
const initialState = {
  // Current view and navigation
  currentView: 'interviews',
  
  // Selection state
  selectedItems: new Set(),
  
  // Loading states
  loading: {
    interviews: false,
    articles: false,
    processing: false,
    activity: false,
  },
  
  // Filter states
  filters: {
    interviews: {
      industry: '',
      status: '',
      priority: '',
      search: '',
    },
    articles: {
      type: '',
      status: '',
      source_interview_id: '',
      search: '',
    },
    processing: {
      status: '',
      dateFrom: '',
      dateTo: '',
      job_type: '',
    },
    activity: {
      activity_type: '',
      entity_type: '',
      task_type: '',
      status: '',
      interview_id: '',
      date_from: '',
      date_to: '',
      search_term: '',
    },
  },
  
  // Pagination states
  pagination: {
    interviews: { page: 1, limit: 20 },
    articles: { page: 1, limit: 20 },
    processing: { page: 1, limit: 50 },
    activity: { page: 1, limit: 25 },
  },
  
  // UI state
  ui: {
    sidebarCollapsed: false,
    theme: 'light',
    showAdvancedFilters: false,
    compactView: false,
  },
  
  // Notifications
  notifications: [],
  
  // Modal states
  modals: {
    addInterview: false,
    csvUpload: false,
    processingDashboard: false,
    batchProcessing: false,
    articleEditor: false,
    confirmDialog: false,
  },
  
  // Error states
  errors: {
    global: null,
    interviews: null,
    articles: null,
    processing: null,
  },
};

// Action types
const ActionTypes = {
  // View and navigation
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW',
  
  // Selection
  TOGGLE_SELECTION: 'TOGGLE_SELECTION',
  SELECT_ALL: 'SELECT_ALL',
  CLEAR_SELECTION: 'CLEAR_SELECTION',
  SET_SELECTION: 'SET_SELECTION',
  
  // Loading
  SET_LOADING: 'SET_LOADING',
  
  // Filters
  SET_FILTER: 'SET_FILTER',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
  RESET_FILTERS: 'RESET_FILTERS',
  
  // Pagination
  SET_PAGINATION: 'SET_PAGINATION',
  RESET_PAGINATION: 'RESET_PAGINATION',
  
  // UI
  SET_UI_STATE: 'SET_UI_STATE',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_THEME: 'SET_THEME',
  
  // Notifications
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
  
  // Modals
  OPEN_MODAL: 'OPEN_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  CLOSE_ALL_MODALS: 'CLOSE_ALL_MODALS',
  
  // Errors
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  CLEAR_ALL_ERRORS: 'CLEAR_ALL_ERRORS',
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_CURRENT_VIEW:
      return {
        ...state,
        currentView: action.payload,
        selectedItems: new Set(), // Clear selection when changing views
      };
    
    case ActionTypes.TOGGLE_SELECTION:
      const newSelection = new Set(state.selectedItems);
      if (newSelection.has(action.payload)) {
        newSelection.delete(action.payload);
      } else {
        newSelection.add(action.payload);
      }
      return {
        ...state,
        selectedItems: newSelection,
      };
    
    case ActionTypes.SELECT_ALL:
      return {
        ...state,
        selectedItems: new Set(action.payload),
      };
    
    case ActionTypes.CLEAR_SELECTION:
      return {
        ...state,
        selectedItems: new Set(),
      };
    
    case ActionTypes.SET_SELECTION:
      return {
        ...state,
        selectedItems: new Set(action.payload),
      };
    
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.key]: action.value,
        },
      };
    
    case ActionTypes.SET_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.category]: {
            ...state.filters[action.category],
            [action.key]: action.value,
          },
        },
        // Reset pagination when filters change
        pagination: {
          ...state.pagination,
          [action.category]: {
            ...state.pagination[action.category],
            page: 1,
          },
        },
      };
    
    case ActionTypes.CLEAR_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.category]: initialState.filters[action.category],
        },
        pagination: {
          ...state.pagination,
          [action.category]: {
            ...state.pagination[action.category],
            page: 1,
          },
        },
      };
    
    case ActionTypes.RESET_FILTERS:
      return {
        ...state,
        filters: initialState.filters,
        pagination: initialState.pagination,
      };
    
    case ActionTypes.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.category]: {
            ...state.pagination[action.category],
            ...action.payload,
          },
        },
      };
    
    case ActionTypes.RESET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.category]: initialState.pagination[action.category],
        },
      };
    
    case ActionTypes.SET_UI_STATE:
      return {
        ...state,
        ui: {
          ...state.ui,
          [action.key]: action.value,
        },
      };
    
    case ActionTypes.TOGGLE_SIDEBAR:
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebarCollapsed: !state.ui.sidebarCollapsed,
        },
      };
    
    case ActionTypes.SET_THEME:
      return {
        ...state,
        ui: {
          ...state.ui,
          theme: action.payload,
        },
      };
    
    case ActionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [
          ...state.notifications,
          {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            ...action.payload,
          },
        ],
      };
    
    case ActionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
      };
    
    case ActionTypes.CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
      };
    
    case ActionTypes.OPEN_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: true,
        },
      };
    
    case ActionTypes.CLOSE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: false,
        },
      };
    
    case ActionTypes.CLOSE_ALL_MODALS:
      return {
        ...state,
        modals: initialState.modals,
      };
    
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.key]: action.value,
        },
      };
    
    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.key]: null,
        },
      };
    
    case ActionTypes.CLEAR_ALL_ERRORS:
      return {
        ...state,
        errors: initialState.errors,
      };
    
    default:
      return state;
  }
}

// Provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Keep state.currentView in sync with the URL.
  // This handles browser back/forward and direct URL loads.
  useEffect(() => {
    const section = location.pathname.split('/')[1] || 'interviews';
    const knownSections = [
      'search', 'interviews', 'articles', 'reports',
      'press-releases', 'podcasts', 'manual-review', 'deep-query',
    ];
    if (knownSections.includes(section) && section !== state.currentView) {
      dispatch({ type: ActionTypes.SET_CURRENT_VIEW, payload: section });
    }
  }, [location.pathname]);

  // Action creators
  const setCurrentView = useCallback((view) => {
    dispatch({ type: ActionTypes.SET_CURRENT_VIEW, payload: view });
    navigate(`/${view}`);
  }, [navigate]);
  
  const toggleSelection = useCallback((id) => {
    dispatch({ type: ActionTypes.TOGGLE_SELECTION, payload: id });
  }, []);
  
  const selectAll = useCallback((items) => {
    dispatch({ type: ActionTypes.SELECT_ALL, payload: items });
  }, []);
  
  const clearSelection = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_SELECTION });
  }, []);
  
  const setSelection = useCallback((items) => {
    dispatch({ type: ActionTypes.SET_SELECTION, payload: items });
  }, []);
  
  const setLoading = useCallback((key, value) => {
    dispatch({ type: ActionTypes.SET_LOADING, key, value });
  }, []);
  
  const setFilter = useCallback((category, key, value) => {
    dispatch({ type: ActionTypes.SET_FILTER, category, key, value });
  }, []);
  
  const clearFilters = useCallback((category) => {
    dispatch({ type: ActionTypes.CLEAR_FILTERS, category });
  }, []);
  
  const resetFilters = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_FILTERS });
  }, []);
  
  const setPagination = useCallback((category, payload) => {
    dispatch({ type: ActionTypes.SET_PAGINATION, category, payload });
  }, []);
  
  const resetPagination = useCallback((category) => {
    dispatch({ type: ActionTypes.RESET_PAGINATION, category });
  }, []);
  
  const setUIState = useCallback((key, value) => {
    dispatch({ type: ActionTypes.SET_UI_STATE, key, value });
  }, []);
  
  const toggleSidebar = useCallback(() => {
    dispatch({ type: ActionTypes.TOGGLE_SIDEBAR });
  }, []);
  
  const setTheme = useCallback((theme) => {
    dispatch({ type: ActionTypes.SET_THEME, payload: theme });
  }, []);
  
  const addNotification = useCallback((notification) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    dispatch({ type: ActionTypes.ADD_NOTIFICATION, payload: { ...notification, id } });
    
    // Auto-remove after specified duration or 5 seconds
    const duration = notification.duration || 5000;
    if (duration > 0) {
      setTimeout(() => {
        dispatch({ type: ActionTypes.REMOVE_NOTIFICATION, payload: id });
      }, duration);
    }
    
    return id;
  }, []);
  
  const removeNotification = useCallback((id) => {
    dispatch({ type: ActionTypes.REMOVE_NOTIFICATION, payload: id });
  }, []);
  
  const clearNotifications = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_NOTIFICATIONS });
  }, []);
  
  const openModal = useCallback((modalName) => {
    dispatch({ type: ActionTypes.OPEN_MODAL, payload: modalName });
  }, []);
  
  const closeModal = useCallback((modalName) => {
    dispatch({ type: ActionTypes.CLOSE_MODAL, payload: modalName });
  }, []);
  
  const closeAllModals = useCallback(() => {
    dispatch({ type: ActionTypes.CLOSE_ALL_MODALS });
  }, []);
  
  const setError = useCallback((key, error) => {
    dispatch({ type: ActionTypes.SET_ERROR, key, value: error });
  }, []);
  
  const clearError = useCallback((key) => {
    dispatch({ type: ActionTypes.CLEAR_ERROR, key });
  }, []);
  
  const clearAllErrors = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ALL_ERRORS });
  }, []);
  
  // Computed values
  const hasSelection = state.selectedItems.size > 0;
  const selectionCount = state.selectedItems.size;
  const selectedArray = Array.from(state.selectedItems);
  
  const value = {
    // State
    state,
    
    // Computed values
    hasSelection,
    selectionCount,
    selectedArray,
    
    // View and navigation
    setCurrentView,
    
    // Selection
    toggleSelection,
    selectAll,
    clearSelection,
    setSelection,
    
    // Loading
    setLoading,
    
    // Filters
    setFilter,
    clearFilters,
    resetFilters,
    
    // Pagination
    setPagination,
    resetPagination,
    
    // UI
    setUIState,
    toggleSidebar,
    setTheme,
    
    // Notifications
    addNotification,
    removeNotification,
    clearNotifications,
    
    // Modals
    openModal,
    closeModal,
    closeAllModals,
    
    // Errors
    setError,
    clearError,
    clearAllErrors,
  };
  
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export default AppContext;